/**
 * Design Tokens - Единая система стилей для PIM Panel
 * Корпоративный стиль: строгий, консервативный, профессиональный
 */

export const tokens = {
  // ============================================
  // SPACING - Отступы
  // ============================================
  pagePadding: 'p-6',
  cardPadding: 'p-6',
  sectionGap: 'space-y-6',
  itemGap: 'space-y-4',
  
  // ============================================
  // CARDS - Карточки
  // ============================================
  card: 'bg-white rounded-lg border border-gray-200 shadow-sm dark:bg-zinc-900 dark:border-zinc-800',
  cardHover: 'hover:shadow-md transition-shadow duration-200',
  cardHeader: 'px-6 py-4 border-b border-gray-200 dark:border-zinc-800',
  cardBody: 'p-6',
  cardFooter: 'px-6 py-4 border-t border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900',

  // ============================================
  // TYPOGRAPHY - Типографика
  // ============================================
  pageTitle: 'text-2xl font-semibold text-gray-900 dark:text-zinc-100',
  pageSubtitle: 'text-sm text-gray-500 mt-1 dark:text-zinc-400',
  sectionTitle: 'text-lg font-medium text-gray-900 dark:text-zinc-100',
  sectionSubtitle: 'text-sm text-gray-500 dark:text-zinc-400',
  label: 'block text-sm font-medium text-gray-700 mb-1 dark:text-zinc-300',
  helperText: 'text-xs text-gray-500 mt-1 dark:text-zinc-400',

  // ============================================
  // BUTTONS - Кнопки
  // ============================================
  btnBase: 'inline-flex items-center justify-center px-4 py-2 rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-zinc-900',
  btnPrimary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
  btnSecondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-indigo-500 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700 dark:hover:bg-zinc-700',
  btnDanger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  btnSuccess: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
  btnGhost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
  btnIcon: 'p-2 rounded-md',

  // Button sizes
  btnSm: 'px-3 py-1.5 text-sm',
  btnMd: 'px-4 py-2 text-sm',
  btnLg: 'px-6 py-3 text-base',

  // ============================================
  // INPUTS - Поля ввода
  // ============================================
  input: 'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors duration-200 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100 dark:placeholder-zinc-500',
  inputError: 'border-red-300 focus:ring-red-500 focus:border-red-500 dark:border-red-800',
  inputDisabled: 'bg-gray-100 cursor-not-allowed dark:bg-zinc-800',
  select: 'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100',
  checkbox: 'h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded dark:border-zinc-600 dark:bg-zinc-800',

  // ============================================
  // TABLES - Таблицы
  // ============================================
  table: 'min-w-full divide-y divide-gray-200 dark:divide-zinc-800',
  tableHeader: 'bg-gray-50 dark:bg-zinc-900',
  tableHeaderCell: 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-zinc-400',
  tableBody: 'bg-white divide-y divide-gray-200 dark:bg-zinc-900 dark:divide-zinc-800',
  tableRow: 'hover:bg-gray-50 transition-colors duration-150 dark:hover:bg-zinc-800/60',
  tableCell: 'px-4 py-3 text-sm text-gray-900 whitespace-nowrap dark:text-zinc-100',
  tableCellSecondary: 'px-4 py-3 text-sm text-gray-500 whitespace-nowrap dark:text-zinc-400',

  // ============================================
  // MODALS - Модальные окна
  // ============================================
  modalBackdrop: 'fixed inset-0 bg-gray-900/50 z-40 transition-opacity duration-200',
  modalContainer: 'fixed inset-0 z-50 flex items-center justify-center p-4',
  modalContent: 'bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden dark:bg-zinc-900',
  modalHeader: 'px-6 py-4 border-b border-gray-200 dark:border-zinc-800',
  modalBody: 'px-6 py-4 overflow-y-auto',
  modalFooter: 'px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 dark:border-zinc-800 dark:bg-zinc-900',
  
  // Modal sizes
  modalSm: 'max-w-sm w-full',
  modalMd: 'max-w-lg w-full',
  modalLg: 'max-w-2xl w-full',
  modalXl: 'max-w-4xl w-full',
  modalFull: 'max-w-6xl w-full',
  
  // ============================================
  // BADGES - Бейджи
  // ============================================
  badgeBase: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
  badgeGray: 'bg-gray-100 text-gray-800 dark:bg-zinc-800 dark:text-zinc-300',
  badgeRed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  badgeYellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
  badgeGreen: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
  badgeBlue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  badgeIndigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
  badgePurple: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',

  // ============================================
  // ALERTS - Уведомления
  // ============================================
  alertBase: 'rounded-md p-4',
  alertSuccess: 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-950/40 dark:border-green-900 dark:text-green-300',
  alertWarning: 'bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-950/40 dark:border-yellow-900 dark:text-yellow-300',
  alertError: 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300',
  alertInfo: 'bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-300',

  // ============================================
  // PAGINATION - Пагинация
  // ============================================
  pagination: 'flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-zinc-800',
  paginationBtn: 'px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700',
  paginationBtnActive: 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700',

  // ============================================
  // EMPTY STATE - Пустое состояние
  // ============================================
  emptyState: 'flex flex-col items-center justify-center py-12 px-4 text-center',
  emptyStateIcon: 'h-16 w-16 text-gray-300 mb-4 dark:text-zinc-600',
  emptyStateTitle: 'text-lg font-medium text-gray-900 mb-2 dark:text-zinc-100',
  emptyStateDescription: 'text-sm text-gray-500 mb-6 max-w-md dark:text-zinc-400',

  // ============================================
  // SKELETON - Скелетон-лоадер
  // ============================================
  skeleton: 'animate-pulse bg-gray-200 rounded dark:bg-zinc-800',
  skeletonText: 'h-4 rounded',
  skeletonTitle: 'h-6 rounded w-1/3',
  skeletonAvatar: 'h-10 w-10 rounded-full',
  skeletonButton: 'h-9 w-24 rounded-md',
  skeletonCard: 'h-32 rounded-lg',
  
  // ============================================
  // SIDEBAR - Боковая панель
  // ============================================
  sidebar: 'bg-white border-r border-gray-200 dark:bg-zinc-900 dark:border-zinc-800',
  sidebarItem: 'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150',
  sidebarItemActive: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  sidebarItemInactive: 'text-gray-700 hover:bg-gray-50 dark:text-zinc-300 dark:hover:bg-zinc-800',

  // ============================================
  // DROPDOWN - Выпадающее меню
  // ============================================
  dropdown: 'absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 dark:bg-zinc-900 dark:ring-zinc-700',
  dropdownItem: 'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 dark:text-zinc-200 dark:hover:bg-zinc-800',

  // ============================================
  // BREADCRUMBS - Хлебные крошки
  // ============================================
  breadcrumbs: 'flex items-center text-sm text-gray-500 mb-4 dark:text-zinc-400',
  breadcrumbItem: 'hover:text-gray-700 transition-colors duration-150 dark:hover:text-zinc-200',
  breadcrumbSeparator: 'mx-2 text-gray-400 dark:text-zinc-500',
  breadcrumbCurrent: 'text-xl font-semibold text-gray-900 dark:text-zinc-100',

  // ============================================
  // TABS - Вкладки
  // ============================================
  tabs: 'flex border-b border-gray-200 dark:border-zinc-800',
  tabList: 'flex border-b border-gray-200 dark:border-zinc-800',
  tab: 'px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-150',
  tabActive: 'border-indigo-500 text-indigo-600 dark:text-indigo-400',
  tabInactive: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:border-zinc-600',

  // ============================================
  // LOADERS - Лоадеры
  // ============================================
  loader: 'animate-spin rounded-full border-b-2 border-indigo-600 dark:border-indigo-400',
  loaderSm: 'h-4 w-4',
  loaderMd: 'h-8 w-8',
  loaderLg: 'h-12 w-12',

  // ============================================
  // KPI CARDS - Карточки метрик
  // ============================================
  kpiCard: 'bg-white rounded-lg border border-gray-200 shadow-sm p-6 dark:bg-zinc-900 dark:border-zinc-800',
  kpiIcon: 'h-12 w-12 rounded-lg flex items-center justify-center',
  kpiValue: 'text-2xl font-bold text-gray-900 dark:text-zinc-100',
  kpiLabel: 'text-sm text-gray-500 dark:text-zinc-400',
  kpiChange: 'text-sm font-medium',
  kpiChangePositive: 'text-green-600 dark:text-green-400',
  kpiChangeNegative: 'text-red-600 dark:text-red-400',
} as const;

// Type for tokens
export type TokenKey = keyof typeof tokens;

// Helper function to combine tokens
export const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Helper to get token value
export const getToken = (key: TokenKey): string => tokens[key];
